import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import { ConfirmationPriseRendezVous, CreneauxDisponiblesCalendrier } from '../types/calendrierVisiteModel'
import { formattageHeure, mappingCreneaux } from '../utils/calendrierVisiteUtils'
import { useDispatch } from 'react-redux'
import { PopinConfirmationState, PopinErreurConfirmationState } from '../types/popinConfirmation'
import { VisiteConcerneeType, VisiteContext } from '../components/VisitesContext'
import { useIntl } from 'react-intl'
import { getVisitesList } from '../api/visitesApi'
import { getNextDate, getSpecificDate } from '../../../../utils/getDays/getNextDate'
import { AppContext } from '../../../../containers/context/AppContext'
import fetchFactory from '../../../../utils/fetchFactory'
import { configEnum } from '../../../../enums/fetchFactoryEnum'
import errorCatcher from '../../../../utils/errorCatcher'
import { formatageDate } from '../../../../utils/formatDate'
import { displaySnackbar } from '../../../reducers/snackBar/actions'
import localStorageEnum from '../../../../enums/localStorageEnum'
import { UserModel } from '../../../../types/user/userModel'

export interface CalendrierContextProps {
	creneauxDisponibles: CreneauxDisponiblesCalendrier,
	loadingCreneaux: boolean,
	loadingConfirmation: boolean,
	confirmationRendezVous: () => void,
	date: Date,
	changeDate: Dispatch<SetStateAction<Date>>,
	creneau: string | null,
	setCreneau: Dispatch<SetStateAction<string | null>>,
	visiteParking: boolean,
	setVisiteParking: Dispatch<SetStateAction<boolean>>,
	minDate?: Date
	maxDate?: Date
}

export const CalendrierContext = createContext<CalendrierContextProps>({
	creneauxDisponibles: {},
	loadingCreneaux: true,
	loadingConfirmation: false,
	confirmationRendezVous: () => {
	},
	date: new Date(),
	changeDate: (date) => {
	},
	creneau: null,
	setCreneau: (creneau) => {
	},
	visiteParking: false,
	setVisiteParking: (visiteParking) => {
	},
	minDate: new Date(),
	maxDate: undefined,
})

interface AcceptingProps {
	offre: VisiteConcerneeType,
	setPopinConfirmation: (popinConfirmation: PopinConfirmationState) => void,
	popinErreurConfirmation: PopinErreurConfirmationState,
	setPopinErreurConfirmation: (popinErreurConfirmation: PopinErreurConfirmationState) => void
	dureeRdv?: number
	isEdle?: boolean
	isEdls?: boolean
	idVisiteEdl?: string
	demandeResidentielId: string
	minDate?: Date
	maxDate?: Date
}

export const CalendrierProvider: React.FC<AcceptingProps> = (
	{
		children,
		offre,
		setPopinConfirmation,
		popinErreurConfirmation,
		setPopinErreurConfirmation,
		dureeRdv,
		isEdle = false,
		isEdls = false,
		idVisiteEdl,
		demandeResidentielId,
		minDate,
		maxDate
	}
) => {
	const { userInfos } = useContext(AppContext)
	const { setIncomingVisits, setIsLoadingVisits } = useContext(VisiteContext)
	const dispatch = useDispatch()
	const intl = useIntl()
	const [creneauxDisponibles, setCreneauxDisponibles] = useState<CreneauxDisponiblesCalendrier>({})
	const [loadingCreneaux, setLoadingCreneaux] = useState(true)
	const [loadingConfirmation, setLoadingConfirmation] = useState(false)
	const [date, changeDate] = useState(new Date(getNextDate()))
	const [creneau, setCreneau] = useState<string | null>(null)
	const [visiteParking, setVisiteParking] = useState<boolean>(!!offre.souhaitVisiteParking)
	const GET_PRISE_RDV = 'getPriseRDV'
	const CONFIRMATION_PRISE_RDV = 'confirmationPriseRDV'

	useEffect(
		() => {
			if (!popinErreurConfirmation.open) {
				setLoadingCreneaux(true)
				fetchFactory(
					'/api/ac_residence/rendezvous/horairesRdv',
					{
						method: configEnum.post,
						body: JSON.stringify({
							id_residence: offre.residence.identifiantCRM,
							id_lot: offre.idLot,
							duree_rdv: dureeRdv,
							id_offre: offre.identifiantCRMOffre,
							demande_resi: demandeResidentielId ?? '',
							is_edle_or_edls: isEdle || isEdls ? true : false,
							is_edle: isEdle,
						})
					}
				)
					.then((response: any) => {
						const creneaux = response.creneaux
						const creneauxDisponiblesTmp = mappingCreneaux(creneaux)

						// Si l'appartement est sous congé, le jour courant est jour + 2
						if (response.appartementSousConge) {
							changeDate(new Date(getSpecificDate(2)))
						}

						setCreneauxDisponibles(creneauxDisponiblesTmp)
					})
					.catch(() => dispatch(errorCatcher(intl.formatMessage({ id: 'visites.popin.priseRDV.calendrier.erreur' }), GET_PRISE_RDV)))
					.finally(() => setLoadingCreneaux(false))
			}
		}, [demandeResidentielId, dispatch, dureeRdv, intl, isEdle, isEdls, offre.idLot, offre.identifiantCRMOffre, offre.residence.identifiantCRM, popinErreurConfirmation]
	)

	const confirmationRendezVous = useCallback(() => {
		const dateFormatee = formatageDate(date.toString(), 'yyyy-MM-dd')
		const user: UserModel = JSON.parse(localStorage.getItem(localStorageEnum.user) ?? '')

		let data: ConfirmationPriseRendezVous = {
			// On met un '!' car le créneau est forcément présent
			date_rdv: `${dateFormatee} ${formattageHeure(creneau!)}`,
			demande_resi: demandeResidentielId ?? '',
			duree_rdv: dureeRdv ?? 45,
			offre: offre.identifiantCRMOffre ?? '',
			genre: offre.genre,
			reference: offre.referenceCRM ?? '',
			lastname: user.lastname ?? '',
			firstname: user.firstname ?? '',
			phone: userInfos.phone ?? '',
			visiteId: offre.visiteId ?? null,
			idLot: offre.idLot,
			visite_parking: visiteParking,
			id_residence: offre.residence.identifiantCRM,
			is_edle: isEdle ?? null,
			is_edls: isEdls ?? null
		}

		if (idVisiteEdl) {
			data = {...data, visiteId: idVisiteEdl}
		}

		setLoadingConfirmation(true)

		fetchFactory(
			'/api/ac_residence/rendezvous/priseRdv',
			{
				method: configEnum.post,
				body: JSON.stringify(data)
			}
		)
			.then(
				() => {
					if (demandeResidentielId.length) {
						getVisitesList(false)
							.then((response: any) => {
								setIncomingVisits(response.visites)
							})
							.finally(() => {
								setIsLoadingVisits(false)
								setPopinConfirmation({
									open: true,
									date: `${dateFormatee} ${formattageHeure(creneau!)}`
								})
							})
					}

				})
			.catch((error) => {
				if (typeof error === 'string') {
					dispatch(displaySnackbar({
						id: CONFIRMATION_PRISE_RDV,
						open: true,
						hideIcon: true,
						message: intl.formatMessage({ id: `${error}.texte` })
					}))
				}
				setCreneau(null)
				setPopinErreurConfirmation({
					open: true,
					error_id: error,
				})
			})
			.finally(() => setLoadingConfirmation(false))
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [date, demandeResidentielId, creneauxDisponibles, creneau, visiteParking])

	return (
		<CalendrierContext.Provider
			value={{
				creneauxDisponibles,
				loadingCreneaux,
				loadingConfirmation,
				confirmationRendezVous,
				date,
				changeDate,
				creneau,
				setCreneau,
				minDate,
				maxDate,
				visiteParking,
				setVisiteParking
			}}
		>
			{children}
		</CalendrierContext.Provider>
	)
}